import type { CityOrderReview } from "../../../api/cityOrders";
import { formatDate } from "../../../api/dates";
import { formatMoney } from "../../../api/money";
import OverrideCheck, { PdfLink } from "../../quotations/reader/OverrideCheck";
import CheckTable from "./CheckTable";
import type { Choice, Choices } from "./cityOrderReview";
import { workOrderMismatch } from "./cityOrderReview";
import HoldList from "./HoldList";

interface Props {
  review: CityOrderReview;
  choices: Choices;
  onChoose: (code: string, choice: Choice | null) => void;
  picked: number | null;
  onPick: (id: number) => void;
  pdfUrl: string | null;
  overriding: boolean;
  overrideReason: string;
  onOverriding: (overriding: boolean) => void;
  onOverrideReason: (reason: string) => void;
}

/** Step 2: what the PO says, what held it, and each line against our BOQ. */
export default function CheckStep(props: Props) {
  const { review, overriding, overrideReason } = props;
  const misread = review.holds.some((hold) => hold.code === "MISREAD");
  return (
    <>
      <Facts review={review} />
      <BoqPicker review={review} picked={props.picked} onPick={props.onPick} />
      <p>
        <PdfLink url={props.pdfUrl} />
      </p>
      <HoldList holds={review.holds} overridden={overriding && overrideReason.trim() !== ""} />
      {misread ? (
        <OverrideCheck
          overriding={overriding}
          reason={overrideReason}
          onOverriding={props.onOverriding}
          onReason={props.onOverrideReason}
          record="PO"
          placeholder="For example: the items were checked against the PDF"
        />
      ) : null}
      {review.warnings.map((warning) => (
        <p key={warning} className="notice warn">
          {warning}
        </p>
      ))}
      <CheckTable lines={review.lines} choices={props.choices} onChoose={props.onChoose} editable={false} />
    </>
  );
}

/** The W/O reads as a chain to its site and bill only while it is that bill's
 * site's; when the server warns it is not, the chain would say it is, so the
 * W/O and the bill checked against are shown apart. */
function Facts({ review }: { review: CityOrderReview }) {
  const o = review.order;
  const contractHeld = review.holds.some((h) => h.code === "WRONG_CONTRACT");
  const apart = workOrderMismatch(review);
  const chain = review.boq && !apart ? ` → ${review.boq.site?.name ?? "its site"} → ${review.boq.name}` : "";
  return (
    <dl className="cm-facts">
      <Fact term="Order" value={`PO ${o.number}`} />
      <Fact term="Dated" value={formatDate(o.po_date)} />
      <Fact term="Contract" value={`${o.contract_no || "—"} ${contractHeld ? "✗" : "✓"}`} />
      <Fact term="W/O" value={`${o.work_order || "—"}${chain}`} />
      {review.boq && apart ? <Fact term="Checked against" value={review.boq.name} /> : null}
      <Fact term="Total" value={`${formatMoney(o.excl_vat)} excl VAT · ${formatMoney(o.total_incl_vat)} incl VAT`} />
    </dl>
  );
}

function Fact({ term, value }: { term: string; value: string }) {
  return (
    <div>
      <dt>{term}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function BoqPicker({ review, picked, onPick }: { review: CityOrderReview; picked: number | null; onPick: (id: number) => void }) {
  if (review.boq !== null || review.choices.length === 0) {
    return null;
  }
  return (
    <label className="move-note">
      Which BOQ is this PO for?
      <select value={picked ?? ""} onChange={(event) => onPick(Number(event.target.value))}>
        <option value="" disabled>
          Pick a BOQ
        </option>
        {review.choices.map((choice) => (
          <option key={choice.id} value={choice.id}>
            {choice.name}
          </option>
        ))}
      </select>
    </label>
  );
}
